<template>
  <section class="admin-shell admin-editor-shell">
    <header class="admin-hero">
      <div>
        <span class="admin-eyebrow">Tech Note Editor</span>
        <h1>{{ selectedNote ? `编辑：${selectedNote.title}` : "技术手记独立编辑器" }}</h1>
        <p>
          这页已经改成正文工作台模式。中间是按前台文章详情页风格呈现的画布，
          右侧只保留设置和结构面板，尽量做到边编辑边看到正式效果。
        </p>
        <p
          v-if="contentMessage || storeContentError"
          class="admin-inline-message"
          :class="{ 'is-error': contentMessageError || Boolean(storeContentError) }"
        >
          {{ contentMessage || storeContentError }}
        </p>
      </div>

      <div class="admin-hero-actions">
        <RouterLink class="admin-link-button" to="/admin">返回后台</RouterLink>
        <RouterLink
          v-if="selectedNote"
          class="admin-link-button"
          :to="`/tech-notes/${selectedNote.slug}`"
        >
          查看前台文章
        </RouterLink>
        <button
          type="button"
          class="admin-secondary-button"
          :disabled="!contentDirty || savingContent"
          @click="saveDraftContent"
        >
          {{ savingContent ? "正在保存..." : "确认保存内容" }}
        </button>
        <button
          type="button"
          class="admin-secondary-button"
          :disabled="!contentDirty || savingContent"
          @click="discardDraftChanges"
        >
          放弃未保存修改
        </button>
      </div>
    </header>

    <section class="admin-panel editor-page-toolbar">
      <div class="editor-page-toolbar__group">
        <span class="admin-section-eyebrow">Current Draft</span>
        <strong>{{ draftTechNotes.length }} 篇草稿文章</strong>
      </div>

      <div class="editor-page-toolbar__group editor-page-toolbar__group--grow">
        <label class="admin-field">
          <span>当前正在编辑</span>
          <select class="admin-select" :value="selectedNoteId" @change="openNote($event.target.value)">
            <option v-for="note in draftTechNotes" :key="note.id" :value="note.id">
              {{ note.title || "未命名文章" }}
            </option>
          </select>
        </label>
      </div>

      <div class="editor-page-toolbar__actions">
        <button type="button" class="admin-secondary-button" @click="createAndOpenNote">
          新增技术手记
        </button>
      </div>
    </section>

    <TechNoteBlockEditor
      v-if="selectedNote"
      :key="`${selectedNote.id}-${editorEpoch}`"
      :note="selectedNote"
      @patch-note="patchNote(selectedNote.id, $event)"
      @remove-note="removeCurrentNote"
    />

    <div v-else class="notes-empty-state">
      <h2>没有可编辑的文章</h2>
      <p>你可以先新建一篇技术手记，或者返回后台总览页重新选择文章。</p>
    </div>

    <div v-if="contentDirty" class="admin-save-bar">
      <div class="admin-save-bar__meta">
        <strong>有未保存修改</strong>
        <span>这里的改动还只是草稿，点击确认保存后才会同步到前台。</span>
      </div>
      <div class="admin-save-bar__actions">
        <button
          type="button"
          class="admin-secondary-button"
          :disabled="savingContent"
          @click="saveDraftContent"
        >
          {{ savingContent ? "正在保存..." : "确认保存内容" }}
        </button>
        <button
          type="button"
          class="admin-danger-button"
          :disabled="savingContent"
          @click="discardDraftChanges"
        >
          放弃修改
        </button>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, ref, watch, watchEffect } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";
import TechNoteBlockEditor from "@/components/TechNoteBlockEditor.vue";
import { useContentStore } from "@/store/contentStore";

const route = useRoute();
const router = useRouter();

const {
  ensureContentLoaded,
  createContentSnapshot,
  saveContentDraft,
  createEmptyTechNote,
  normalizeContentBlocks,
  renderContentBlocksToHtml,
  countCodeBlocksInContentBlocks,
  slugify,
  ensureUniqueSlug,
  savingContent,
  contentError: storeContentError,
} = useContentStore();

const contentMessage = ref("");
const contentMessageError = ref(false);
const draftNavGroups = ref({ left: [], right: [] });
const draftModuleIntros = ref({});
const draftTechNotes = ref([]);
const draftLightVideos = ref([]);
const contentDirty = ref(false);
const editorEpoch = ref(0);

const selectedNoteId = computed(() => String(route.params.noteId || ""));
const selectedNote = computed(
  () => draftTechNotes.value.find((item) => item.id === selectedNoteId.value) || null
);

const setContentMessage = (message, isError = false) => {
  contentMessage.value = message;
  contentMessageError.value = isError;
};

const syncDraftFromStore = () => {
  const snapshot = createContentSnapshot();
  draftNavGroups.value = snapshot.navGroups;
  draftModuleIntros.value = snapshot.moduleIntros || {};
  draftTechNotes.value = snapshot.techNotes;
  draftLightVideos.value = snapshot.lightVideos || [];
  contentDirty.value = false;
  editorEpoch.value += 1;
};

const patchNote = (noteId, patch) => {
  const note = draftTechNotes.value.find((item) => item.id === noteId);
  if (!note) {
    return;
  }

  const hasContentHtmlPatch = Object.prototype.hasOwnProperty.call(patch, "contentHtml");
  const hasContentBlocksPatch = Object.prototype.hasOwnProperty.call(patch, "contentBlocks");
  const hasCodeBlocksPatch = Object.prototype.hasOwnProperty.call(patch, "codeBlocks");

  Object.assign(note, patch);
  note.categorySlug = slugify(note.categorySlug || note.categoryTitle || "uncategorized");
  note.slug = ensureUniqueSlug(note.slug || note.title, draftTechNotes.value, note.id);

  if (hasContentBlocksPatch) {
    note.contentBlocks = normalizeContentBlocks(note.contentBlocks);
  }

  if (hasContentHtmlPatch) {
    note.contentHtml = String(patch.contentHtml || "");
  } else if (hasContentBlocksPatch && !String(note.contentHtml || "").trim()) {
    note.contentHtml = renderContentBlocksToHtml(note.contentBlocks);
  }

  if (hasCodeBlocksPatch) {
    note.codeBlocks = Number(patch.codeBlocks || 0);
  } else if (hasContentBlocksPatch) {
    note.codeBlocks = countCodeBlocksInContentBlocks(note.contentBlocks);
  }

  contentDirty.value = true;
};

const saveDraftContent = async () => {
  const result = await saveContentDraft({
    navGroups: draftNavGroups.value,
    moduleIntros: draftModuleIntros.value,
    techNotes: draftTechNotes.value,
    lightVideos: draftLightVideos.value,
  });

  if (!result.ok) {
    setContentMessage(result.error, true);
    return;
  }

  syncDraftFromStore();
  setContentMessage("内容已确认保存，前台现在会显示这次修改。");
};

const discardDraftChanges = () => {
  if (!contentDirty.value) {
    return;
  }

  if (!window.confirm("确认放弃当前未保存的内容草稿吗？")) {
    return;
  }

  syncDraftFromStore();
  setContentMessage("未保存的草稿修改已放弃。");
};

const openNote = (noteId) => {
  if (!noteId || String(noteId) === selectedNoteId.value) {
    return;
  }

  router.push({
    name: "admin-tech-note-editor",
    params: {
      noteId,
    },
  });
};

const createAndOpenNote = () => {
  const note = createEmptyTechNote(draftTechNotes.value);
  draftTechNotes.value.unshift(note);
  contentDirty.value = true;
  openNote(note.id);
  setContentMessage("新文章已经加入草稿区，确认保存后才会发布到前台。");
};

const removeCurrentNote = () => {
  if (!selectedNote.value) {
    return;
  }

  if (!window.confirm("确认删除这篇技术手记吗？")) {
    return;
  }

  const index = draftTechNotes.value.findIndex((item) => item.id === selectedNote.value.id);
  if (index < 0) {
    return;
  }

  draftTechNotes.value.splice(index, 1);
  contentDirty.value = true;
  const nextNote = draftTechNotes.value[0];

  if (nextNote) {
    openNote(nextNote.id);
  } else {
    router.push("/admin");
  }

  setContentMessage("文章已从草稿区移除，确认保存后才会真正从前台删除。");
};

watch(
  () => route.params.noteId,
  () => {
    if (!selectedNote.value && draftTechNotes.value.length) {
      openNote(draftTechNotes.value[0].id);
    }
  }
);

onMounted(async () => {
  const result = await ensureContentLoaded();
  if (result?.ok === false && result?.error) {
    setContentMessage(result.error, true);
  }

  syncDraftFromStore();

  if (!selectedNote.value && draftTechNotes.value.length) {
    openNote(draftTechNotes.value[0].id);
  }
});

watchEffect(() => {
  document.title = selectedNote.value
    ? `圣域空间 | 编辑 ${selectedNote.value.title}`
    : "圣域空间 | 技术手记编辑器";
});
</script>
